import { type ComponentType, Suspense, lazy } from "react";
import type { EmojiPickerProps } from "stream-chat-react/emojis";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import type { Language } from "Translations/languageConfig";
import { useLanguage } from "Translations/useLanguage";

/**
 * emoji-mart ships UI strings for these locales only. Slovak is missing, so it falls back to
 * Czech — the closest language, and the same fallback Stream's own chat i18n uses.
 */
type EmojiMartLocale = "cs" | "de" | "en" | "pl" | "vi";

const appLanguageToEmojiMartLocale = {
  cs: "cs",
  de: "de",
  en: "en",
  pl: "pl",
  sk: "cs",
  vi: "vi",
} as const satisfies Record<Language, EmojiMartLocale>;

/**
 * Bundled locale loaders. Without an explicit `i18n`, emoji-mart fetches every non-English
 * locale from the jsdelivr CDN at runtime — a third-party dependency (CSP, offline, availability)
 * for a few kilobytes of strings we already have installed. English is built into emoji-mart, so
 * it needs no loader.
 */
const emojiMartI18nLoaders: Record<
  Exclude<EmojiMartLocale, "en">,
  () => Promise<unknown>
> = {
  cs: () => import("@emoji-mart/data/i18n/cs.json").then((m) => m.default),
  de: () => import("@emoji-mart/data/i18n/de.json").then((m) => m.default),
  pl: () => import("@emoji-mart/data/i18n/pl.json").then((m) => m.default),
  vi: () => import("@emoji-mart/data/i18n/vi.json").then((m) => m.default),
};

const getEmojiMartI18n = (locale: EmojiMartLocale) => {
  if (locale === "en") {
    return undefined;
  }
  return emojiMartI18nLoaders[locale];
};

/**
 * Stream's plugin pulls in the emoji-mart core. The chat page itself is not code-split, so without
 * this the core would join the main bundle for every user, chat or not. Loading it lazily defers
 * the core to the first render of a composer; emoji data and locale strings are already loaded on
 * first open by emoji-mart itself.
 *
 * A failed chunk load (flaky network, stale tab after a deploy) must not reach the root error
 * boundary and replace the whole app with the error screen over an optional button. It degrades
 * to no emoji button instead, which is exactly the composer before this feature existed.
 */
const NoEmojiPicker: ComponentType<EmojiPickerProps> = () => null;

const StreamEmojiPicker = lazy<ComponentType<EmojiPickerProps>>(() =>
  import("stream-chat-react/emojis")
    .then((m) => ({ default: m.EmojiPicker }))
    .catch((error: unknown) => {
      logError(
        error instanceof Error ? error : new Error(String(error)),
        { source: "ChatEmojiPicker chunk load" },
        false,
      );
      return { default: NoEmojiPicker };
    }),
);

/**
 * Reserves the button's box while the chunk loads so the textarea pill doesn't render full width
 * and then shrink. Reusing Stream's wrapper class picks up the same sizing rules (Stream's and
 * ours in ChatOverrides.css) as the real button, so the two can't drift apart.
 */
const EmojiPickerPlaceholder = () => (
  <div className="str-chat__message-textarea-emoji-picker" aria-hidden />
);

/**
 * The emoji keyboard button + popover in the message composer, backed by Stream's `EmojiPicker`
 * plugin (emoji-mart underneath). Stream's component owns the open/close state and inserts the
 * picked emoji at the caret via the text composer; this wrapper only localises the picker and
 * tunes it for our layout.
 *
 * The popover stays open after a pick on every viewport, so several emojis can go in one pass.
 */
export const ChatEmojiPicker = () => {
  const { currentLanguage } = useLanguage();

  const locale = appLanguageToEmojiMartLocale[currentLanguage];

  return (
    <Suspense fallback={<EmojiPickerPlaceholder />}>
      <StreamEmojiPicker
        pickerProps={{
          theme: "light",
          locale,
          i18n: getEmojiMartI18n(locale),
          previewPosition: "none",
          skinTonePosition: "search",
        }}
      />
    </Suspense>
  );
};
